import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';

import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {IStore} from 'redux/interface';
import {
  getReferrals,
  getTranslations,
  referralsAsyncActions,
  removeReferrals,
  sendReferrals
} from 'redux/pageAsyncActions';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';

import ReferralsIcon from 'assets/image/menu-auth/referrals.png';
import NotFoundImg from 'assets/image/common/notFound.png';

import Loader from 'components/loader';
import NotFoundInfo from 'components/not-found-info';
import {ICmService} from '../../redux/cm-service/interfaces';
import CmCardSection from '../../components/cm-card-section';
import {setSendReferralsStatus} from '../../redux/cm-service/actions';
import {IShareReferrals, ReferralsShare} from '../../components/referrals-share/ReferralsShare';

import './styles.scss';

export const Referrals: React.FunctionComponent = () => {
  const {
    referrals,
    sendReferralsStatus
  } = useSelector((state: IStore) => state.cm_service);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const {
    settings
  } = useSelector((state: IStore) => state.auth);
  const dispatch = useDispatch();
  const history = useNavigate();
  const intl = useIntl();
  const [isLoadCards, setIsLoadCards] = useState(true);
  const [isShareMode, setIsShareMode] = useState(false);
  const [checkedCards, setCheckedCards] = useState<number[]>([]);
  const [recipientInfo, setRecipientInfo] = useState<IShareReferrals>({
    emails: '',
    emailHtml: '',
    templateId: 0,
    toSelf: false
  });

  const translationKey = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Referrals.toLowerCase());

    return page?.translationKey || TranslationKeys.Referrals;
  }, [settings]);

  useGetTranslations({translationKey});

  const title = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Referrals.toLowerCase());

    return page?.title || Pages.Referrals;
  }, [settings]);

  useEffect(() => {
    dispatch(referralsAsyncActions[getReferrals]());
    setIsLoadCards(false);
  }, []);

  useEffect(() => {
    setCheckedCards([]);
    setIsShareMode(false);
  }, [sendReferralsStatus]);

  useEffect(() => {
    if (!isShareMode) {
      setCheckedCards([]);
      setRecipientInfo({
        emails: '',
        emailHtml: '',
        templateId: 0,
        toSelf: false
      });
    }
  }, [isShareMode]);

  function removeAll() {
    const cardsId: number[] = [];

    referrals.map((card: ICmService) => {
      return cardsId.push(card.srvId);
    });
    dispatch(referralsAsyncActions[removeReferrals](cardsId, true));
  }

  function validateModel(): boolean {
    return Boolean(checkedCards.length && recipientInfo.emails && recipientInfo.templateId);
  }

  function handleSendReferrals() {
    if (validateModel()) {
      dispatch(referralsAsyncActions[sendReferrals]({
        srvIds: checkedCards,
        emails: recipientInfo.emails,
        templateId: recipientInfo.templateId as number,
        emailText: recipientInfo.emailHtml,
        toSelf: recipientInfo.toSelf
      }));
    }
  }

  function handleChangeShareMode(mode: boolean) {
    setIsShareMode(mode);
    if (mode) {
      setCheckedCards(referrals.map(r => r.srvId));
    }
  }

  function handleDoneSend() {
    dispatch(setSendReferralsStatus(false));
  }

  function handleChangeShareReferrals(referralModel: IShareReferrals) {
    setRecipientInfo(referralModel);
  }

  function handleOpenCard(srvId: number) {
    if (isShareMode) {
      if (checkedCards.includes(srvId)) {
        setCheckedCards(checkedCards.filter(item => item !== srvId));
      } else {
        setCheckedCards([...checkedCards, srvId]);
      }
    } else {
      history(`/cmresource/${srvId}`);
    }
  }

  function renderReferralsCards() {
    return referrals.length === 0
      ? <>
        <NotFoundInfo
          title={intl.formatMessage({
            id: 'gritx.referrals.notFound',
            defaultMessage: 'No Referrals Added Yet'
          })}
          description={intl.formatMessage({
            id: 'gritx.referrals.tapSave',
            defaultMessage: 'Tap "Add to referrals" button when you view the resource to save your referrals here'
          })}
          image={NotFoundImg}
        />
      </>
      : <>
        <CmCardSection
          showLoader={loading.includes(getReferrals)}
          cards={referrals}
          checkedCards={checkedCards}
          onClickCard={handleOpenCard}
        />
      </>;
  }

  return <div className="referrals container">
    {
      !loading.includes(getTranslations) && <>
        {loading.includes(sendReferrals) && <Loader nested/>}
        <h1 className="referrals__title"><img src={ReferralsIcon} alt="" className="referrals__icon"/>
          <FormattedMessage
            id={`gritx.common.navigation.${title}`}
            defaultMessage={title}
          />
        </h1>
        <div className="referrals__share-line">
          <ReferralsShare
            isDisabledRemoveAll={!referrals.some(card => card.material)}
            isDisabledShare={!referrals.some(card => card.material)}
            isDisabledSend={!validateModel()}
            onSend={handleSendReferrals}
            onChange={handleChangeShareReferrals}
            onRemoveAll={removeAll}
            onShareMode={handleChangeShareMode}
            sendStatus={sendReferralsStatus}
            onDone={handleDoneSend}
            checkedCards={checkedCards}
          />
        </div>
        {
          sendReferralsStatus
            ? <div className="referrals__done">
              <div className="referrals__image-wrapper">
              </div>
              <p className="referrals__done-text">
                <FormattedMessage
                  id="gritx.referrals.success"
                  defaultMessage="Your referrals were successfully sent!"
                />
              </p>
            </div>
            : <div className="referrals__cards">
              {
                loading.includes(getReferrals) || isLoadCards ? <Loader nested/> : renderReferralsCards()
              }
            </div>
        }
      </>
    }
  </div>;
};
